"use client";
import React, { Suspense, useState } from "react";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import ApplyCareer from "./apply-career";
import { CardSkeleton } from "./card-skeleton";

export default function CareerCard({ career }: any) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCareerId, setSelectedCareerId] = useState(null);

  const toggleModal = (careerId = null) => {
    setSelectedCareerId(careerId);
    setIsModalOpen(!isModalOpen);
  };

  return (
    <>
      <Suspense
        fallback={
          <div>
            <CardSkeleton />
          </div>
        }
      >
        <div
          data-aos="fade-up"
          className="group relative overflow-hidden rounded-2xl border border-slate-200/60 bg-white/70 p-6 backdrop-blur-sm transition-all duration-300 hover:border-indigo-200/60 hover:shadow-lg hover:shadow-indigo-500/[0.06] max-w-[100%] my-3"
        >
          {/* Subtle gradient accent on hover */}
          <div className="pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-br from-indigo-50/0 to-violet-50/0 opacity-0 transition-opacity duration-300 group-hover:opacity-100 group-hover:from-indigo-50/40 group-hover:to-violet-50/30" />

          <div className="relative flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 shadow-md shadow-indigo-500/20">
                <img
                  src="/logo-32-32.png"
                  alt="Company Logo"
                  className="w-7 h-7 rounded-lg"
                />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900">
                  {career?.designation}
                </h2>
                <p className="text-sm text-slate-500">Botbyte AI</p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-600">
                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                {career?.exp}
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-600">
                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {career?.salary}
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-violet-50 px-3 py-1 text-xs font-medium text-violet-600">
                Remote
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
                Full-time
              </span>
            </div>
          </div>

          {/* Description */}
          <div className="relative mt-4 border-t border-slate-100 pt-4">
            <p className="text-sm leading-relaxed text-slate-600">
              {career?.description.trim().substring(0, 120)}...{" "}
              <button
                className="text-indigo-500 font-medium hover:text-indigo-600 transition-colors"
                onClick={() => toggleModal(career.id)}
              >
                view more
              </button>
            </p>
          </div>

          {/* Apply button */}
          <div className="relative mt-5">
            <button
              onClick={() => toggleModal(career.id)}
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 px-6 py-2.5 text-sm font-semibold text-white shadow-md shadow-indigo-500/15 transition-all duration-200 hover:shadow-lg hover:shadow-indigo-500/25 hover:brightness-110 active:scale-[0.97]"
            >
              Easy Apply
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
              </svg>
            </button>
          </div>
        </div>

        {isModalOpen && (
          <div className="fixed z-50 inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="relative bg-white rounded-2xl shadow-2xl shadow-slate-900/20 w-full max-w-2xl max-h-[80vh] overflow-y-auto p-1">
              <ApplyCareer careerId={selectedCareerId} />
              <button
                onClick={toggleModal}
                className="absolute top-4 right-4 flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-500 transition-colors hover:bg-slate-200 hover:text-slate-700"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </Suspense>
    </>
  );
}
